import React from 'react';
import TransformSection from './common/transform-section';
import { registerInspector } from './inspector-registry';
import ColorField from './shared/color-field';
import FillGradientSection from './shared/fill-gradient-section';
import InspectorRow from './shared/inspector-row';
import InspectorSection from './shared/inspector-section';
import NumberField from './shared/number-field';
import SlotFilterSection from './shared/slot-filter-section';
import { useCommonFabricProp } from './shared/use-common-fabric-prop';
import { useFillGradient } from './shared/use-fill-gradient';
import type { ObjectInspectorProps } from './types';

const MIXED = '(разное)';

/**
 * Inspector for the fillable shapes that carry no type-specific geometry controls —
 * circle, ellipse, triangle and every polygon the Shapes menu builds (right
 * triangle, pentagon, hexagon, star, diamond, all `type: 'polygon'`). They share one
 * component because their inspectable surface is identical; `rect` keeps its own
 * because of the per-corner radius section. Before this they fell through to
 * FallbackInspector and had no fill controls at all.
 */
const ShapeInspector: React.FC<ObjectInspectorProps> = ({ objects, editor }) => {
  const fill = useCommonFabricProp<string>(objects, 'fill', editor);
  const stroke = useCommonFabricProp<string>(objects, 'stroke', editor);
  const strokeWidth = useCommonFabricProp<number>(objects, 'strokeWidth', editor);
  const opacity = useCommonFabricProp<number>(objects, 'opacity', editor);
  const gradient = useFillGradient(objects, editor);
  // When the gradient is on, `fill` is a gradient (not a hex string), so the plain
  // fill picker is meaningless — the colors are edited in the Gradient section.
  const gradientActive = gradient.enabled.value === true || gradient.enabled.isMixed;

  return (
    <>
      <TransformSection objects={objects} editor={editor} />
      <InspectorSection title="Внешний вид">
        {!gradientActive && (
          <InspectorRow label="Заливка">
            <ColorField
              value={fill.isMixed ? undefined : fill.value}
              onChange={fill.setValue}
              placeholder={fill.isMixed ? MIXED : undefined}
            />
          </InspectorRow>
        )}
        <InspectorRow label="Обводка">
          <ColorField
            value={stroke.isMixed ? undefined : stroke.value}
            onChange={stroke.setValue}
            placeholder={stroke.isMixed ? MIXED : undefined}
          />
        </InspectorRow>
        <InspectorRow label="Толщина">
          <NumberField
            value={strokeWidth.isMixed ? undefined : strokeWidth.value}
            onChange={strokeWidth.setValue}
            min={0}
            placeholder={strokeWidth.isMixed ? MIXED : undefined}
          />
        </InspectorRow>
        <InspectorRow label="Прозрачность">
          <NumberField
            value={opacity.isMixed ? undefined : opacity.value}
            onChange={opacity.setValue}
            step={0.05}
            min={0}
            max={1}
            placeholder={opacity.isMixed ? MIXED : undefined}
          />
        </InspectorRow>
      </InspectorSection>
      <FillGradientSection control={gradient} />
      <SlotFilterSection objects={objects} editor={editor} />
    </>
  );
};

export default ShapeInspector;

registerInspector('circle', ShapeInspector);
registerInspector('ellipse', ShapeInspector);
registerInspector('triangle', ShapeInspector);
registerInspector('polygon', ShapeInspector);
